import React, { useMemo, useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';

import type { OpportunityRelatedExtraGuestSurcharge } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { getRoomRateHref } from '../../shared/roomRateHref';
import type { RoomRateSelection } from '../surcharges/surchargeUtils';

import EgsApplyRoomRateSection from './EgsApplyRoomRateSection';
import EgsRatePlanStep from './EgsRatePlanStep';
import EgsSurchargeMappingStep from './EgsSurchargeMappingStep';
import type { EgsNothingToApplyReason } from './buildEgsApplyPlan';
import { canApplyEgsCandidate } from './egsApplyCandidates';
import { type EgsReviewRow, buildEgsApplyRows } from './egsApplyRows';
import { buildEgsMatchByRoomLabel } from './egsRoomMatcher';
import { compareEgsRowStatus, getEgsRowStatus, isEgsRowDefaultExpanded } from './egsRowStatus';
import { groupEgsByRoom } from './egsUtils';
import {
  EGS_STEP_HEADING,
  EGS_STEP_LABEL,
  EGS_WIZARD_STEPS,
  type EgsWizardStep,
  getEgsStepBlockReason,
} from './egsWizard';
import { useEgsApplyPlan } from './useEgsApplyPlan';

interface Props {
  egsByRoomRateId: Record<string, Array<App.ExtraGuestSurcharges>>;
  onClose: () => void;
  propertyId: string;
  roomRateSelections: Array<RoomRateSelection>;
  roomTypes: Array<App.RoomType>;
  surcharges: Array<OpportunityRelatedExtraGuestSurcharge>;
  /** Only used to link to the room rate editor, so a missing vendor drops the link rather than the row. */
  vendorId?: string;
}

const NOTHING_TO_APPLY_MESSAGE: Record<EgsNothingToApplyReason, string> = {
  all_free_of_charge:
    'Every Salesforce surcharge on this offer is Free of Charge, so there are no amounts to write. Saving zeros would show a surcharge on every date in the date pickers while charging nothing.',
  already_matches: 'Every room rate already has what Salesforce says. There is nothing to apply.',
};

export default function ApplyEgsModal({
  egsByRoomRateId,
  onClose,
  propertyId,
  roomRateSelections,
  roomTypes,
  surcharges,
  vendorId,
}: Props) {
  const roomGroups = useMemo(() => groupEgsByRoom(surcharges), [surcharges]);
  const matchByRoomLabel = useMemo(
    () =>
      buildEgsMatchByRoomLabel(
        roomGroups.map((group) => group.roomLabel),
        roomTypes,
      ),
    [roomGroups, roomTypes],
  );

  const {
    applicablePlans,
    blockedPlans,
    candidates,
    failedWrites,
    fxRatesBlocker,
    gatedPlans,
    handleApply,
    hasDiscardedGateAnswers,
    isApplying,
    nothingToApplyReason,
    plans,
    ratePlanId,
    ratePlans,
    selectRatePlan,
    selectedRoomRateIdsByRoomLabel,
    setArchiveDecision,
    setPayableAtProperty,
    setSlotWinner,
    setWriteCurrencyMode,
    toggleRoomRate,
  } = useEgsApplyPlan({
    egsByRoomRateId,
    matchByRoomLabel,
    onApplied: onClose,
    propertyId,
    roomGroups,
    roomRateSelections,
  });

  const steps = EGS_WIZARD_STEPS;
  const [step, setStep] = useState<EgsWizardStep>(() => steps[0]);
  const [blockReason, setBlockReason] = useState<string | undefined>(undefined);

  // Distinct, since two surcharges can tick the same room rate.
  const selectedRoomRateCount = useMemo(
    () => new Set(Object.values(selectedRoomRateIdsByRoomLabel).flat()).size,
    [selectedRoomRateIdsByRoomLabel],
  );

  /** A room rate no surcharge is ticked against has no plan, so filtering on the plan is the whole rule. */
  const reviewRows = useMemo(
    () =>
      buildEgsApplyRows({ candidates, plans, selectedRoomRateIdsByRoomLabel })
        .filter((row): row is EgsReviewRow => !!row.plan)
        .sort((a, b) => compareEgsRowStatus(getEgsRowStatus(a.plan), getEgsRowStatus(b.plan))),
    [candidates, plans, selectedRoomRateIdsByRoomLabel],
  );

  const stepIndex = Math.max(steps.indexOf(step), 0);
  const activeStep = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;

  const goToStep = (nextStep: EgsWizardStep) => {
    const nextIndex = steps.indexOf(nextStep);

    // Forwards only, so going back is never gated on the answer being complete. Every skipped step is asked,
    // since the stepper is non-linear and checking only the current one walks past the surcharge guard.
    if (nextIndex > stepIndex) {
      const reason = steps
        .slice(stepIndex, nextIndex)
        .map((skippedStep) => getEgsStepBlockReason({ ratePlanId, selectedRoomRateCount, step: skippedStep }))
        .find(Boolean);

      if (reason) {
        setBlockReason(reason);
        return;
      }
    }

    setBlockReason(undefined);
    setStep(nextStep);
  };

  // Only meaningful once a plan is chosen: before that the candidate list is empty by construction.
  const isDeadEnd = !!ratePlanId && (!!nothingToApplyReason || !candidates.some(canApplyEgsCandidate));
  // A dead end on one plan is not a dead end on the offer, so the way back to the pick has to stay on screen.
  const canChangeRatePlan = ratePlans.length > 1;

  const deadEndAlert = (
    <Alert severity="info">
      {nothingToApplyReason
        ? NOTHING_TO_APPLY_MESSAGE[nothingToApplyReason]
        : // A name can no longer reach here, since an unreadable one offers every room.
          'No Salesforce surcharge on this offer has a live guest type to write. Archived rows, and guest types we do not recognise, are both left out.'}
    </Alert>
  );

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Apply Salesforce extra guest surcharges</DialogTitle>

      <DialogContent dividers>
        {isDeadEnd && !canChangeRatePlan ? (
          deadEndAlert
        ) : isDeadEnd ? (
          // Dead end on this plan only, so the pick goes back on screen rather than closing the dialog.
          <Stack spacing={2}>
            {deadEndAlert}
            <EgsRatePlanStep onSelectRatePlan={selectRatePlan} ratePlanId={ratePlanId} ratePlans={ratePlans} />
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Stepper activeStep={stepIndex} nonLinear>
              {steps.map((wizardStep) => (
                <Step key={wizardStep}>
                  <StepButton color="inherit" onClick={() => goToStep(wizardStep)}>
                    <StepLabel>{EGS_STEP_LABEL[wizardStep]}</StepLabel>
                  </StepButton>
                </Step>
              ))}
            </Stepper>

            {activeStep === 'review' ? (
              <Alert icon={false} severity="info">
                <Typography variant="subtitle2">
                  {`You are about to write ${applicablePlans.length} room ${applicablePlans.length === 1 ? 'rate' : 'rates'}. Check each one below before applying.`}
                </Typography>
              </Alert>
            ) : (
              <Typography variant="body2">{EGS_STEP_HEADING[activeStep]}</Typography>
            )}

            {blockReason && <Alert severity="warning">{blockReason}</Alert>}

            {/* Show rate problems once the wizard reaches room selection. */}
            {activeStep !== 'ratePlan' && fxRatesBlocker === 'fx_rates_loading' && (
              <Alert severity="info">
                Still loading exchange rates, so the amounts for this offer’s currency are not ready yet.
              </Alert>
            )}

            {activeStep !== 'ratePlan' && fxRatesBlocker === 'fx_rates_unavailable' && (
              <Alert severity="error">
                Exchange rates could not be loaded, so any room rate priced in another currency cannot be applied right
                now. Reload the page to try again.
              </Alert>
            )}

            {activeStep === 'ratePlan' && (
              <EgsRatePlanStep onSelectRatePlan={selectRatePlan} ratePlanId={ratePlanId} ratePlans={ratePlans} />
            )}

            {/* Said on the step that caused it, not the one the answers disappeared from. */}
            {activeStep === 'surcharges' && hasDiscardedGateAnswers && (
              <Alert severity="info">
                The decisions you made on the review step were cleared, because changing which rooms a surcharge covers
                changes which questions get asked. You will be asked again for anything still unresolved.
              </Alert>
            )}

            {activeStep === 'surcharges' && (
              <EgsSurchargeMappingStep
                candidates={candidates}
                selectedRoomRateIdsByRoomLabel={selectedRoomRateIdsByRoomLabel}
                toggleRoomRate={toggleRoomRate}
              />
            )}

            {activeStep === 'review' && (
              <Stack spacing={1}>
                {/* Above the gates, since retrying is the next thing the CM does. */}
                {failedWrites.length > 0 && (
                  <Alert severity="error">
                    {`${failedWrites.length} room ${failedWrites.length === 1 ? 'rate was' : 'rates were'} not written: ${failedWrites
                      .map((failure) => failure.roomLabel)
                      .join(
                        ', ',
                      )}. Anything that saved has dropped off this list, so applying again sends only ${failedWrites.length === 1 ? 'this one' : 'these'}.`}
                  </Alert>
                )}

                {/* Two alerts: one is work the CM can do here, the other is not. Both sort to the top. */}
                {gatedPlans.length > 0 && (
                  <Alert severity="warning">
                    {`${gatedPlans.length} room ${gatedPlans.length === 1 ? 'rate needs' : 'rates need'} a decision from you, listed first. You can also apply the rest without ${gatedPlans.length === 1 ? 'it' : 'them'}.`}
                  </Alert>
                )}

                {blockedPlans.length > 0 && (
                  <Alert severity="error">
                    {`${blockedPlans.length} room ${blockedPlans.length === 1 ? 'rate cannot' : 'rates cannot'} be written and will be skipped. Each one shows what to fix.`}
                  </Alert>
                )}

                {reviewRows.length === 0 ? (
                  <Alert severity="info">No room rate is left to review. Go back and tick at least one.</Alert>
                ) : (
                  reviewRows.map((row) => (
                    <EgsApplyRoomRateSection
                      key={row.roomRateId}
                      defaultExpanded={isEgsRowDefaultExpanded(getEgsRowStatus(row.plan), reviewRows.length)}
                      onReselectSurcharges={() => goToStep('surcharges')}
                      roomRateHref={getRoomRateHref({
                        propertyId,
                        roomRateId: row.roomRateId,
                        roomTypeId: row.plan.roomTypeId,
                        vendorId,
                      })}
                      row={row}
                      setArchiveDecision={setArchiveDecision}
                      setPayableAtProperty={setPayableAtProperty}
                      setSlotWinner={setSlotWinner}
                      setWriteCurrencyMode={setWriteCurrencyMode}
                    />
                  ))
                )}
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        {/* Only once there is a plan to count against, or it reads as a verdict on an unasked question. */}
        <Typography color="text.secondary" variant="body2">
          {isDeadEnd || !ratePlanId
            ? ''
            : `${applicablePlans.length} room ${applicablePlans.length === 1 ? 'rate' : 'rates'} will be written`}
        </Typography>
        <Stack direction="row" gap={1}>
          <Button onClick={onClose} variant="text">
            Cancel
          </Button>
          {!isDeadEnd && stepIndex > 0 && (
            <Button disabled={isApplying} onClick={() => goToStep(steps[stepIndex - 1])} variant="text">
              Back
            </Button>
          )}
          {!isDeadEnd && !isLastStep ? (
            <Button onClick={() => goToStep(steps[stepIndex + 1])} variant="contained">
              Continue
            </Button>
          ) : (
            <Button
              disabled={isApplying || isDeadEnd || applicablePlans.length === 0}
              onClick={handleApply}
              variant="contained"
            >
              {isApplying ? 'Applying…' : 'Apply'}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
